import emojiData from '../../EmojiPicker/emojiDataExtended'

import type { CompletionItem, CompletionProvider } from './types'

// Emoji completion — the first consumer of the generic menu. Reuses the
// already-bundled @emoji-mart/data (same dataset the picker uses); no new
// dependency and no external search index to depend on.

type RawEmoji = {
  id: string
  name: string
  keywords?: string[]
  skins: { native: string }[]
}
const data = emojiData as unknown as {
  emojis: Record<string, RawEmoji>
  aliases?: Record<string, string>
}

type Indexed = { id: string; native: string; name: string; keywords: string[] }

// Flatten the dataset once at module load.
const index: Indexed[] = Object.values(data.emojis)
  .map(e => ({
    id: e.id,
    native: e.skins?.[0]?.native ?? '',
    name: e.name.toLowerCase(),
    keywords: e.keywords ?? [],
  }))
  .filter(e => e.native !== '')
const aliasToId = data.aliases ?? {}

// Lower score = better match: exact id, id prefix, exact keyword, name prefix,
// then substrings. Infinity = no match.
function score(e: Indexed, term: string): number {
  if (e.id === term) return 0
  if (e.id.startsWith(term)) return 1
  if (e.keywords.some(k => k === term)) return 2
  if (e.name.startsWith(term)) return 3
  if (e.id.includes(term)) return 4
  if (e.keywords.some(k => k.startsWith(term))) return 5
  if (e.name.includes(term)) return 6
  return Infinity
}

// ponytail: linear scan of ~1900 in-memory rows per keystroke. Ceiling: trivial
// at this size; if the dataset grows or mentions add a big list, precompute a
// prefix trie or debounce the query.
export const emojiProvider: CompletionProvider = {
  trigger: ':',
  minChars: 2,
  query(rawTerm: string): CompletionItem[] {
    const term = rawTerm.toLowerCase()
    const aliasId = aliasToId[term] // an exact alias should rank at the very top
    const scored: { e: Indexed; s: number }[] = []
    for (const e of index) {
      const s = aliasId && e.id === aliasId ? 0 : score(e, term)
      if (s !== Infinity) scored.push({ e, s })
    }
    scored.sort(
      (a, b) =>
        a.s - b.s ||
        a.e.id.length - b.e.id.length ||
        a.e.id.localeCompare(b.e.id)
    )
    return scored.slice(0, 30).map(({ e }) => ({
      id: e.id,
      label: `:${e.id}:`,
      value: e.native,
      preview: e.native,
    }))
  },
}
